import React, { useEffect, useState, useContext } from "react";
import Header from "../../Components/Header";
import ModifySearch from "./ModifySearch";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation, Autoplay } from "swiper/modules";
import {
  getHotelSearch,
  getHotelStaticData,
} from "../../Services/HotelSearchApi";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../Routes/AuthProvider";

const Results = () => {
  const navigate = useNavigate();
  const { userName } = useContext(AuthContext);
  const [searchData, setSearchData] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [pricingTraceId, setPricingTraceId] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const searchSessionRef = React.useRef(0);

  const [showModifyModal, setShowModifyModal] = useState(false);
  const [sortBy, setSortBy] = useState("CHEAPEST");

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  const formatPolicyDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  const getCancellationSummary = (hotel) => {
    const policies = hotel.cancellationPolicies || hotel.cancellationPolicy;
    if (!Array.isArray(policies) || policies.length === 0) {
      return null;
    }

    // Sort policies chronologically by fromDate
    const sorted = [...policies].sort((a, b) => new Date(a.fromDate) - new Date(b.fromDate));
    
    // Find the first policy where a penalty applies (penaltyAmount > 0)
    const penaltyPolicy = sorted.find(p => (p.penaltyAmount || p.chargeValue || 0) > 0);
    
    if (penaltyPolicy && penaltyPolicy.fromDate) {
      const penaltyDate = new Date(penaltyPolicy.fromDate);
      const now = new Date();
      
      // If the penalty date is in the future, then we are currently in the free cancellation window!
      if (penaltyDate > now) {
        return {
          text: `Free cancellation before ${formatPolicyDate(penaltyPolicy.fromDate)}`,
          isFree: true
        };
      } else {
        // If the penalty date is in the past, then it is currently non-refundable
        return {
          text: "Non-Refundable",
          isFree: false
        };
      }
    }

    // If there are no policies with a penalty, it might be free cancellation all the way (or check refundable flag)
    if (hotel.refundable) {
      return {
        text: "Refundable",
        isFree: true
      };
    }

    return {
      text: "Non-Refundable",
      isFree: false
    };
  };

  const totalGuestsText = React.useMemo(() => {
    if (!searchData) return "";
    const roomsCount = searchData.roomGuests?.length || 1;
    const adultsCount = searchData.roomGuests?.reduce((sum, r) => sum + r.noOfAdults, 0) || 1;
    const childrenCount = searchData.roomGuests?.reduce((sum, r) => sum + (r.noOfChilds || 0), 0) || 0;
    return `${roomsCount} Room${roomsCount > 1 ? "s" : ""}, ${adultsCount} Adult${adultsCount > 1 ? "s" : ""}${childrenCount > 0 ? `, ${childrenCount} Child${childrenCount > 1 ? "ren" : ""}` : ""}`;
  }, [searchData]);

  const exportToExcel = () => {
    const headers = [
      "Hotel Name",
      "Star Rating",
      "Price",
      "Address",
      "Cancellation Policy",
      "Policy Type",
      "Meal Basis",
      "Room Name",
    ];
    const rows = filteredHotels.map((h) => [
      h.hotelName,
      `${h.starRating || 0} Star`,
      `INR ${Math.round(h.hotelMinPrice || 0)}`,
      h.address || "",
      h.refundable ? "Refundable" : "Non-Refundable",
      h.policyType || "",
      h.mealBasis || "",
      h.roomName || "",
    ]);
    const csvRows = [headers.join(",")];
    for (const row of rows) {
      const escapedRow = row.map((val) => `"${String(val).replace(/"/g, '""')}"`);
      csvRows.push(escapedRow.join(","));
    }
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    const filename = `Hotel_Results_${searchData?.destinationName || "List"}.csv`;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Alternative Hotel States
  const [showAlternativeModal, setShowAlternativeModal] = useState(false);
  const [pendingHotelSelection, setPendingHotelSelection] = useState(null);
  const [bestAlternativeHotel, setBestAlternativeHotel] = useState(null);
  const [alternativeRemarks, setAlternativeRemarks] = useState("");
  const [selectedAlternativeOption, setSelectedAlternativeOption] = useState("Recommended");
  const [selectedReason, setSelectedReason] = useState("");
  const [customRemarks, setCustomRemarks] = useState("");

  // Filter States
  const [hotelNameFilter, setHotelNameFilter] = useState("");
  const [filters, setFilters] = useState({
    price: [],
    rating: [],
    cancellation: [],
    meals: [],
    amenities: [],
    gst: [],
  });

  const fetchSearchData = () => {
    const data = sessionStorage.getItem("hotelSearchData");
    if (data) {
      const parsed = JSON.parse(data);
      setSearchData(parsed);
      if (parsed.starRatings && parsed.starRatings.length > 0) {
        setFilters((prev) => ({
          ...prev,
          rating: parsed.starRatings.map(String),
        }));
      } else if (parsed.starRating) {
        setFilters((prev) => ({
          ...prev,
          rating: [parsed.starRating.toString()],
        }));
      } else {
        setFilters((prev) => ({
          ...prev,
          rating: [],
        }));
      }
      // Increment trigger to force re-search even if data appears unchanged
      setSearchTrigger((prev) => prev + 1);
    }
  };

  useEffect(() => {
    fetchSearchData();
  }, []);

  useEffect(() => {
    searchSessionRef.current += 1;
    const currentSession = searchSessionRef.current;
    if (searchData) {
      performSearch(currentSession);
    }
  }, [searchData, searchTrigger]);

  const performSearch = async (session) => {
    setLoading(true);
    setHotels([]);

    const hotelIds = searchData.hotelIds || [];
    if (hotelIds.length === 0) {
      setLoading(false);
      return;
    }

    const chunkArray = (array, size) => {
      const chunks = [];
      for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
      }
      return chunks;
    };

    const batches = chunkArray(hotelIds, 100);
    let completedBatchesCount = 0;

    batches.forEach(async (batch) => {
      try {
        // 1. Fetch Static Data for batch
        const staticData = await getHotelStaticData(batch);

        // 2. Fetch Pricing Data for batch
        const searchPayload = {
          traceId: searchData.traceId,
          checkInDate: searchData.checkInDate,
          checkOutDate: searchData.checkOutDate,
          currency: searchData.currency,
          HotelIds: batch,
          roomGuests: searchData.roomGuests,
          nationality: searchData.nationality,
          travelType: searchData.travelType,
          corporateCode: searchData.corporateCode,
          corporateTraveler: searchData.corporateTraveler,
          userName: userName || "",
        };
        const pricingResponse = await getHotelSearch(searchPayload);
        const pricingHotels = pricingResponse?.hotels || [];
        const newTraceId = pricingResponse?.traceId;

        if (session !== searchSessionRef.current) return;

        if (newTraceId) {
          setPricingTraceId((prev) => prev || newTraceId);
        }

        // 3. Merge Data: Only include hotels that are present in the search response
        const mergedBatch = staticData
          .map((s) => {
            const pricing = pricingHotels.find(
              (p) => p.hotelCode === s.hotelid,
            );
            return pricing ? { ...pricing, ...s, hotelid: s.hotelid, traceId: newTraceId } : null;
          })
          .filter((h) => h !== null);

        if (session === searchSessionRef.current) {
          if (mergedBatch.length > 0) {
            setHotels((prev) => {
              const existingIds = new Set(prev.map((h) => h.hotelid));
              const uniqueNew = mergedBatch.filter((h) => !existingIds.has(h.hotelid));
              return [...prev, ...uniqueNew];
            });
            setLoading(false);
          }
        }
      } catch (error) {
        console.error("Batch fetch failed:", error);
      } finally {
        if (session === searchSessionRef.current) {
          completedBatchesCount += 1;
          if (completedBatchesCount === batches.length) {
            setLoading(false);
          }
        }
      }
    });
  };

  // useEffect(() => {
  //   if (!loading && hotels && hotels.length > 0) {
  //     console.log("=== Checking Alternatives for All Loaded Hotels ===");
  //     hotels.forEach((hotel) => {
  //       const alternatives = hotels
  //         .map((h) => {
  //           if (h.hotelid === hotel.hotelid) return null;
  //           if (h.policyType === "OutOfPolicy") return null;
  //           if (h.starRating !== hotel.starRating) return null;
  //           if ((h.hotelMinPrice || 0) >= (hotel.hotelMinPrice || 0)) return null;
  //           if (!h.latitude || !h.longitude || !hotel.latitude || !hotel.longitude)
  //             return null;

  //           const distance = calculateDistance(
  //             hotel.latitude,
  //             hotel.longitude,
  //             h.latitude,
  //             h.longitude
  //           );

  //           if (distance <= 3) {
  //             return { ...h, calculatedDistance: distance.toFixed(1) };
  //           }
  //           return null;
  //         })
  //         .filter(Boolean);

  //       if (alternatives.length > 0) {
  //         console.log("hemanth")
  //         console.log(
  //           `Hotel "${hotel.hotelName}" has ${alternatives.length} alternatives:`,
  //           alternatives.map((alt) => `${alt.hotelName} (Price: ${alt.hotelMinPrice}, Distance: ${alt.calculatedDistance}km)`)
  //         );
  //       } else {
  //         console.log(`${hotel.hotelName} has no alternatives.`);
  //       }
  //     });
  //     console.log("====================================================");
  //   }
  // }, [loading, hotels]);

  const calculateNights = (inDate, outDate) => {
    if (!inDate || !outDate) return 1;
    try {
      const start = new Date(inDate);
      const end = new Date(outDate);
      const diff = end.getTime() - start.getTime();
      const nights = Math.ceil(diff / (1000 * 3600 * 24));
      return nights > 0 ? nights : 1;
    } catch (e) {
      return 1;
    }
  };

  const nightsCount = React.useMemo(() => {
    return calculateNights(searchData?.checkInDate, searchData?.checkOutDate);
  }, [searchData]);

  const handleFilterToggle = (category, value) => {
    setFilters((prev) => {
      const current = prev[category];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      
      // Automatically set sortBy to "CHEAPEST" if a price filter is checked
      if (category === "price" && next.length > 0) {
        setSortBy("CHEAPEST");
      }
      
      return { ...prev, [category]: next };
    });
  };

  const filterOptions = React.useMemo(() => {
    // Dynamic Price Ranges
    const prices = hotels
      .map((h) => Math.round(h.hotelMinPrice || 0))
      .filter(Boolean)
      .sort((a, b) => a - b);
    const minP = prices[0] || 0;
    const maxP = prices[prices.length - 1] || 0;

    let dynamicPriceRanges = [];
    if (prices.length > 0) {
      // Helper to round to nice numbers
      const roundToNiceNumber = (num) => {
        if (num < 1000) return Math.round(num / 50) * 50;
        if (num < 3000) return Math.round(num / 100) * 100;
        if (num < 10000) return Math.round(num / 500) * 500;
        return Math.round(num / 1000) * 1000;
      };

      const getPercentile = (arr, pct) => {
        const index = Math.floor((arr.length - 1) * pct);
        return arr[index];
      };

      // We generate raw boundaries at percentiles to divide into up to 6 ranges
      const rawBoundaries = [
        getPercentile(prices, 0.17),
        getPercentile(prices, 0.34),
        getPercentile(prices, 0.50),
        getPercentile(prices, 0.67),
        getPercentile(prices, 0.83),
      ];

      // Round them to nice numbers
      let boundaries = rawBoundaries.map(roundToNiceNumber);

      // Remove duplicates and keep sorted
      boundaries = [...new Set(boundaries)].sort((a, b) => a - b);

      // Fallback if not enough unique boundaries
      if (boundaries.length < 2) {
        const step = Math.ceil((maxP - minP) / 4);
        boundaries = [];
        if (step > 0) {
          for (let i = 1; i < 4; i++) {
            const val = roundToNiceNumber(minP + i * step);
            if (val > minP && val < maxP) {
              boundaries.push(val);
            }
          }
        }
      }

      // Build range objects
      const ranges = [];
      const len = boundaries.length;

      for (let i = 0; i <= len; i++) {
        let start = 0;
        let end = Infinity;
        let label = "";

        if (i === 0) {
          end = boundaries[0] || maxP;
          start = 0;
          const count = hotels.filter(
            (h) => Math.round(h.hotelMinPrice || 0) <= end
          ).length;
          label = `Up to ₹${end.toLocaleString()} (${count})`;
        } else if (i === len) {
          start = (boundaries[i - 1] || minP) + 1;
          end = 999999999;
          const count = hotels.filter(
            (h) => Math.round(h.hotelMinPrice || 0) >= start
          ).length;
          label = `₹${start.toLocaleString()}+ (${count})`;
        } else {
          start = (boundaries[i - 1] || minP) + 1;
          end = boundaries[i] || maxP;
          const count = hotels.filter((h) => {
            const p = Math.round(h.hotelMinPrice || 0);
            return p >= start && p <= end;
          }).length;
          label = `₹${start.toLocaleString()} - ₹${end.toLocaleString()} (${count})`;
        }

        ranges.push({
          id: i.toString(),
          label,
          start,
          end,
        });
      }
      // console.log("[PriceRanges] Generated price ranges:", ranges);
      dynamicPriceRanges = ranges;
    }

    return {
      priceRanges: dynamicPriceRanges,
      ratings: [...new Set(hotels.map((h) => h.starRating))]
        .filter(Boolean)
        .sort((a, b) => b - a),
      meals: [...new Set(hotels.map((h) => h.mealBasis))].filter(Boolean),
      cancellations: ["Refundable", "Non Refundable"],
      // gstOptions: ["GST Reclaim"],
      gstOptions: [],
      amenities: (() => {
        const all = hotels.flatMap((h) => h.amenities || []);
        const counts = all.reduce((acc, f) => {
          acc[f] = (acc[f] || 0) + 1;
          return acc;
        }, {});
        return Object.keys(counts)
          .sort((a, b) => counts[b] - counts[a])
          .slice(0, 10);
      })(),
    };
  }, [hotels, nightsCount]);

  const filteredHotels = React.useMemo(() => {
    // console.log("[filteredHotels] Recalculating! Full list length:", hotels.length, "filters.price:", filters.price);
    const list = hotels.filter((hotel) => {
      // 0. Hotel Name Search Filter
      if (hotelNameFilter.trim() !== "") {
        if (!hotel.hotelName?.toLowerCase().includes(hotelNameFilter.toLowerCase().trim())) {
          return false;
        }
      }

      // 1. Price Filter
      if (filters.price.length > 0) {
        const price = Math.round(hotel.hotelMinPrice || 0);
        const priceMatches = filters.price.some((rangeId) => {
          const range = filterOptions.priceRanges.find((r) => String(r.id) === String(rangeId));
          const matches = range ? price >= range.start && price <= range.end : false;
          // console.log(`[PriceFilter] Hotel: ${hotel.hotelName}, Price: ${price}, Range: ${range?.label} (id: ${rangeId}), Matches: ${matches}`);
          return matches;
        });
        if (!priceMatches) return false;
      }

      // 2. Rating Filter
      if (filters.rating.length > 0) {
        if (!filters.rating.includes(hotel.starRating?.toString()))
          return false;
      }

      // 3. Cancellation Filter
      if (filters.cancellation.length > 0) {
        const isRef = hotel.refundable ? "Refundable" : "Non Refundable";
        if (!filters.cancellation.includes(isRef)) return false;
      }

      // 4. Meals Filter
      if (filters.meals.length > 0) {
        if (!filters.meals.includes(hotel.mealBasis)) return false;
      }

      // 5. Amenities Filter
      if (filters.amenities.length > 0) {
        const match = filters.amenities.some((f) =>
          hotel.amenities?.some((a) =>
            a.toLowerCase().includes(f.toLowerCase()),
          ),
        );
        if (!match) return false;
      }

      // 6. GST Filter
      // if (filters.gst.length > 0) {
      //   if (filters.gst.includes("GST Reclaim") && !hotel.gst) {
      //     return false;
      //   }
      // }

      return true;
    });

    // Apply sorting (force lowest to highest when price filter is active)
    if (sortBy === "CHEAPEST" || filters.price.length > 0) {
      return [...list].sort((a, b) => (a.hotelMinPrice || 0) - (b.hotelMinPrice || 0));
    } else if (sortBy === "RATING") {
      return [...list].sort((a, b) => (b.starRating || 0) - (a.starRating || 0));
    }

    return list;
  }, [hotels, filters, filterOptions, sortBy, hotelNameFilter]);

  const AMENITY_MAP = {
    wifi: "wifi.png",
    wireless: "wifi.png",
    internet: "wifi.png",
    net: "wifi.png",
    parking: "car-parking.png",
    valet: "car-parking.png",
    airport: "car-parking.png",
    taxi: "car-parking.png",
    shuttle: "car-parking.png",
    transport: "car-parking.png",
    car: "car-parking.png",
    breakfast: "breakfast.png",
    meal: "breakfast.png",
    dining: "breakfast.png",
    restaurant: "breakfast.png",
    cafe: "breakfast.png",
    coffee: "breakfast.png",
    food: "breakfast.png",
    kitchen: "breakfast.png",
    bar: "breakfast.png",
    gym: "gym.png",
    fitness: "gym.png",
    health: "gym.png",
    exercise: "gym.png",
    yoga: "gym.png",
    swimming: "swimming.png",
    pool: "swimming.png",
    spa: "spa.png",
    massage: "spa.png",
    wellness: "spa.png",
    sauna: "spa.png",
    steam: "spa.png",
    treatment: "spa.png",
    elevator: "wifi.png", // Fallback to wifi or dedicated icon if available
    business: "gym.png",
    meeting: "gym.png",
    room: "breakfast.png",
    laundry: "car-parking.png",
    doctor: "spa.png",
    kids: "swimming.png",
    child: "swimming.png",
    baby: "swimming.png",
  };

  const getAmenityIcons = (amenities) => {
    if (!amenities || amenities.length === 0) {
      return ["wifi.png", "car-parking.png"];
    }

    const matched = [];
    for (const am of amenities) {
      const key = Object.keys(AMENITY_MAP).find((k) =>
        am.toLowerCase().includes(k),
      );
      if (key && !matched.includes(AMENITY_MAP[key])) {
        matched.push(AMENITY_MAP[key]);
      }
    }

    if (matched.length === 0) {
      return ["wifi.png", "car-parking.png", "breakfast.png"];
    }

    return matched.slice(0, 4);
  };

  // Haversine formula to calculate distance between two coordinates in km
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
   // console.log(lat1, lon1, lat2, lon2)
    if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
  
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const handleChooseRoom = (hotel) => {
    // Find alternatives based on criteria
    const alternativeHotels = hotels
      .map((h) => {
        // Must not be the same hotel
        if (h.hotelid === hotel.hotelid) return null;
        // Must be strictly in-policy
        if (h.policyType === "OutOfPolicy") return null;
        // Same star rating
        if (h.starRating !== hotel.starRating) return null;
        // Must have a lower price than the selected hotel
        if ((h.hotelMinPrice || 0) >= (hotel.hotelMinPrice || 0)) return null;
        // Must have valid coordinates
        if (!h.latitude || !h.longitude || !hotel.latitude || !hotel.longitude)
          return null;
        // console.log(hotel)
        const distance = calculateDistance(
          hotel.latitude,
          hotel.longitude,
          h.latitude,
          h.longitude
        );

        if (distance <= 3) {
          return { ...h, calculatedDistance: distance.toFixed(1) };
        }
        return null;
      })
      .filter(Boolean);

    // console.log("Alternative Hotel Names:", alternativeHotels.map(h => h.hotelName));

    if (alternativeHotels.length > 0) {
    
      alternativeHotels.sort(
        (a, b) => (a.hotelMinPrice || 0) - (b.hotelMinPrice || 0)
      );
      const bestAlternative = alternativeHotels[0];

      // Show modal since we found an alternative (user requested to show even for in-policy selections)
      setPendingHotelSelection(hotel);
      setBestAlternativeHotel(bestAlternative);
      setAlternativeRemarks(""); // Reset remarks
      setSelectedReason("");     // Reset reason
      setCustomRemarks("");      // Reset custom remarks
      setShowAlternativeModal(true);
      return; // Prevent immediate navigation
    }

    proceedWithHotelSelection(hotel);
  };

  const proceedWithHotelSelection = (hotel, selectedOption = "Recommended") => {
    let finalRemarks = "";
    if (selectedOption === "original") {
      if (!selectedReason) {
        alert("Please select a reason to proceed with your current selection.");
        document.getElementById("alternativeRemarksReason")?.focus();
        return;
      }
      if (selectedReason === "Other" && (!customRemarks || !customRemarks.trim())) {
        alert("Please enter custom remarks to proceed with your current selection.");
        document.getElementById("alternativeRemarksCustom")?.focus();
        return;
      }
      finalRemarks = selectedReason === "Other" ? customRemarks : selectedReason;
    }

    // Create the required request body for logging
    const priceDiff = bestAlternativeHotel
      ? Math.max(0, (pendingHotelSelection?.hotelMinPrice || 0) - (bestAlternativeHotel.hotelMinPrice || 0))
      : 0;

    const originalHotel = pendingHotelSelection ;

    const logObject = {
      referenceNo: searchData?.traceId || "",
      lowFareHotelJson: JSON.stringify(hotel),
      selectedOption: selectedOption,
      remark: finalRemarks || "",
      createdOn: new Date().toISOString(),
      recommendedHotelName: bestAlternativeHotel?.hotelName || "",
      recommendedPrice: bestAlternativeHotel?.hotelMinPrice || 0,
      selectedHotelName: originalHotel?.hotelName || "",
      selectedPrice: originalHotel?.hotelMinPrice || 0,
      savingPrice: selectedOption === "Recommended" ? priceDiff : 0,
      missingPrice: selectedOption === "original" ? priceDiff : 0,
      createdBy: userName,
      corporateName: localStorage.getItem("selectedUserId") || "",
      corporateCode: localStorage.getItem("selectedCorporateCode") || "",
      recommendedHotelJson: bestAlternativeHotel ? JSON.stringify(bestAlternativeHotel) : "",
      selectedHotelJson: JSON.stringify(originalHotel),
      userName: userName,
    };
    sessionStorage.setItem("hotelAlternativeLog", JSON.stringify(logObject));

    // console.log(hotel,logObject);
   
    const updatedSearchData = {
      ...searchData,
      traceId: hotel.traceId || pricingTraceId || searchData?.traceId || "",
    };

    sessionStorage.setItem("selectedHotel", JSON.stringify(hotel));
    sessionStorage.setItem(
      "hotelSearchData",
      JSON.stringify(updatedSearchData),
    );

    if (finalRemarks) {
      sessionStorage.setItem("hotelRemarks", finalRemarks);
    } else {
      sessionStorage.removeItem("hotelRemarks");
    }

    setShowAlternativeModal(false);

    // Navigate to hotel detail page
    navigate("/hotel/hoteldetail");
  };

  return (
    <>
      <style>{`
        .Results_hotel .text-success, 
        .modal .text-success {
          color: #28a745 !important;
        }
        .Results_hotel .bg-success, 
        .modal .bg-success {
          background-color: #28a745 !important;
        }
        .Results_hotel .border-success, 
        .modal .border-success {
          border-color: #28a745 !important;
        }
      `}</style>
      <Header></Header>

      <section className="Results_hotel">

        {/* Modify Search Modal */}
        {showModifyModal && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.6)", zIndex: 1060 }}
          >
            <div className="modal-dialog modal-xl modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg rounded-4 p-4">
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                  <div className="d-flex align-items-center">
                    <div
                  
                    ></div>
                    <h4 className="modal-title fw-bold m-0" style={{ color: "#110942", fontSize: "1.6rem" }}>
                      Modify Result
                    </h4>
                  </div>
                  <button
                    type="button"
                    className="btn-close fs-5"
                    onClick={() => setShowModifyModal(false)}
                    style={{ cursor: "pointer" }}
                  ></button>
                </div>
                <div className="modal-body p-0">
                  <ModifySearch
                    onSearch={() => {
                      setShowModifyModal(false);
                      fetchSearchData();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-12 col-md-3 col-lg-3 mb-4 mb-md-0">
            <div className="box_shadow sticky">
              <div class="accordion" id="kt_accordion_1">
                {/* Search by Hotel Name */}
                <div class="accordion-item">
                  <h2 class="accordion-header" id="kt_accordion_1_header_name_filter">
                    <button
                      class="accordion-button fs-4 fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#kt_accordion_1_body_name_filter"
                      aria-expanded="true"
                      aria-controls="kt_accordion_1_body_name_filter"
                    >
                      Search Hotel Name
                    </button>
                  </h2>
                  <div
                    id="kt_accordion_1_body_name_filter"
                    class="accordion-collapse collapse show"
                    aria-labelledby="kt_accordion_1_header_name_filter"
                    data-bs-parent="#kt_accordion_1"
                  >
                    <div class="accordion-body">
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by hotel name..."
                          value={hotelNameFilter}
                          onChange={(e) => setHotelNameFilter(e.target.value)}
                          style={{ paddingRight: "30px", height: "40px" }}
                        />
                        {hotelNameFilter && (
                          <button
                            className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-muted p-0 me-3"
                            onClick={() => setHotelNameFilter("")}
                            style={{ border: "none", background: "none", textDecoration: "none", zIndex: 10 }}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {filterOptions.priceRanges.length > 0 && (
                  <div class="accordion-item">
                    <h2 class="accordion-header" id="kt_accordion_1_header_1">
                      <button
                        class="accordion-button fs-4 fw-semibold collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#kt_accordion_1_body_1"
                        aria-expanded="true"
                        aria-controls="kt_accordion_1_body_1"
                      >
                        Price Range
                      </button>
                    </h2>
                    <div
                      id="kt_accordion_1_body_1"
                      class="accordion-collapse collapse show"
                      aria-labelledby="kt_accordion_1_header_1"
                      data-bs-parent="#kt_accordion_1"
                    >
                      <div class="accordion-body">
                        {filterOptions.priceRanges.map((range) => (
                          <div class="form-check w-100" key={range.id}>
                            <input
                              class="form-check-input"
                              type="checkbox"
                              checked={filters.price.includes(range.id)}
                              onChange={() =>
                                handleFilterToggle("price", range.id)
                              }
                              id={`price-${range.id}`}
                            />
                            <label
                              class="form-check-label"
                              htmlFor={`price-${range.id}`}
                            >
                              {range.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div class="accordion-item">
                  <h2 class="accordion-header" id="kt_accordion_1_header_2">
                    <button
                      className={`accordion-button fs-4 fw-semibold ${filters.rating.length > 0 ? "" : "collapsed"}`}
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#kt_accordion_1_body_2"
                      aria-expanded={filters.rating.length > 0 ? "true" : "false"}
                      aria-controls="kt_accordion_1_body_2"
                    >
                      Star Rating
                    </button>
                  </h2>
                  <div
                    id="kt_accordion_1_body_2"
                    className={`accordion-collapse collapse ${filters.rating.length > 0 ? "show" : ""}`}
                    aria-labelledby="kt_accordion_1_header_2"
                    data-bs-parent="#kt_accordion_1"
                  >
                    <div class="accordion-body">
                      {filterOptions.ratings.length > 0 ? (
                        filterOptions.ratings.map((rating) => (
                          <div className="form-check w-100" key={rating}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={filters.rating.includes(
                                rating.toString(),
                              )}
                              onChange={() =>
                                handleFilterToggle("rating", rating.toString())
                              }
                              id={`rating-${rating}`}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`rating-${rating}`}
                            >
                              {[...Array(rating)].map((_, i) => (
                                <i key={i} className="star"></i>
                              ))}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted small">No ratings available</p>
                      )}
                    </div>
                  </div>
                </div>

                <div class="accordion-item">
                  <h2 class="accordion-header" id="kt_accordion_1_header_3">
                    <button
                      class="accordion-button fs-4 fw-semibold collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#kt_accordion_1_body_3"
                      aria-expanded="false"
                      aria-controls="kt_accordion_1_body_3"
                    >
                      Cancellation Type
                    </button>
                  </h2>
                  <div
                    id="kt_accordion_1_body_3"
                    class="accordion-collapse collapse"
                    aria-labelledby="kt_accordion_1_header_3"
                    data-bs-parent="#kt_accordion_1"
                  >
                    <div class="accordion-body">
                      <div class="form-check w-100">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          checked={filters.cancellation.includes(
                            "Non Refundable",
                          )}
                          onChange={() =>
                            handleFilterToggle("cancellation", "Non Refundable")
                          }
                          id="flexCheckDefault13"
                        />
                        <label
                          class="form-check-label"
                          for="flexCheckDefault13"
                        >
                          Non Refundable
                        </label>
                      </div>

                      <div class="form-check w-100">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          checked={filters.cancellation.includes("Refundable")}
                          onChange={() =>
                            handleFilterToggle("cancellation", "Refundable")
                          }
                          id="flexCheckChecked2"
                        />
                        <label class="form-check-label" for="flexCheckChecked2">
                          Refundable
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                  <h2 class="accordion-header" id="kt_accordion_1_header_4">
                    <button
                      class="accordion-button fs-4 fw-semibold collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#kt_accordion_1_body_4"
                      aria-expanded="false"
                      aria-controls="kt_accordion_1_body_4"
                    >
                      Meal Type
                    </button>
                  </h2>
                  <div
                    id="kt_accordion_1_body_4"
                    class="accordion-collapse collapse"
                    aria-labelledby="kt_accordion_1_header_4"
                    data-bs-parent="#kt_accordion_1"
                  >
                    <div class="accordion-body">
                      {filterOptions.meals.length > 0 ? (
                        filterOptions.meals.map((meal) => (
                          <div className="form-check w-100" key={meal}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={filters.meals.includes(meal)}
                              onChange={() => handleFilterToggle("meals", meal)}
                              id={`meal-${meal.replace(/\s+/g, "-")}`}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`meal-${meal.replace(/\s+/g, "-")}`}
                            >
                              {meal}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted small">
                          No meal options available
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* <div class="accordion-item">
                  <h2 class="accordion-header" id="kt_accordion_1_header_gst">
                    <button
                      class="accordion-button fs-4 fw-semibold collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#kt_accordion_1_body_gst"
                      aria-expanded="false"
                      aria-controls="kt_accordion_1_body_gst"
                    >
                      GST Reclaim
                    </button>
                  </h2>
                  <div
                    id="kt_accordion_1_body_gst"
                    class="accordion-collapse collapse"
                    aria-labelledby="kt_accordion_1_header_gst"
                    data-bs-parent="#kt_accordion_1"
                  >
                    <div class="accordion-body">
                      {filterOptions.gstOptions.map((opt) => (
                        <div className="form-check w-100" key={opt}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={filters.gst.includes(opt)}
                            onChange={() => handleFilterToggle("gst", opt)}
                            id={`gst-${opt.replace(/\s+/g, "-")}`}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`gst-${opt.replace(/\s+/g, "-")}`}
                          >
                            {opt}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div> */}

                <div class="accordion-item">
                  <h2 class="accordion-header" id="kt_accordion_1_header_5">
                    <button
                      class="accordion-button fs-4 fw-semibold collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#kt_accordion_1_body_5"
                      aria-expanded="false"
                      aria-controls="kt_accordion_1_body_5"
                    >
                      Show Hotels With
                    </button>
                  </h2>
                  <div
                    id="kt_accordion_1_body_5"
                    class="accordion-collapse collapse"
                    aria-labelledby="kt_accordion_1_header_5"
                    data-bs-parent="#kt_accordion_1"
                  >
                    <div class="accordion-body">
                      {filterOptions.amenities.length > 0 ? (
                        filterOptions.amenities.map((amenity) => (
                          <div className="form-check w-100" key={amenity}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={filters.amenities.includes(amenity)}
                              onChange={() =>
                                handleFilterToggle("amenities", amenity)
                              }
                              id={`amenity-${amenity.replace(/\s+/g, "-")}`}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`amenity-${amenity.replace(/\s+/g, "-")}`}
                            >
                              {amenity}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted small">
                          No amenities available
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-9 col-lg-9">
            {/* Gold Summary & Sort Wrapper */}
            <div className="results-summary-wrapper">
              {/* Summary Card */}
              <div className="summary-card shadow-sm">
                <div className="summary-left">
                  <span style={{ textTransform: "capitalize" }}>{searchData?.destinationName || "New Delhi"}</span>
                  <span className="summary-pipe">||</span>
                  <span>{totalGuestsText}</span>
                  <span className="summary-pipe">||</span>
                  <span>
                    <span className="summary-highlight">{formatDateDisplay(searchData?.checkInDate)}</span>
                    {" to "}
                    <span className="summary-highlight">{formatDateDisplay(searchData?.checkOutDate)}</span>
                  </span>
                  <span className="summary-pipe">||</span>
                  <span>{nightsCount} Night{nightsCount > 1 ? "s" : ""}</span>
                  <span className="summary-pipe">||</span>
                  <span>
                    hotels : <span className="summary-highlight">{filteredHotels.length}</span>
                  </span>
                </div>
                <div className="summary-right">
                  <span className="modify-btn" onClick={() => setShowModifyModal(true)}>
                    Modify Result <i className="fas fa-arrow-right ms-1"></i>
                  </span>
                </div>
              </div>

              {/* Sort Tabs Card */}
              <div className="sort-tabs-card shadow-sm">
                <div className="sort-btn-group">
                  <button
                    className={`sort-tab-btn ${sortBy === "CHEAPEST" ? "active" : ""}`}
                    onClick={() => setSortBy((prev) => (prev === "CHEAPEST" ? "" : "CHEAPEST"))}
                  >
                    <i className="fa fa-inr"></i> Cheapest
                  </button>
                  <button
                    className={`sort-tab-btn ${sortBy === "RATING" ? "active" : ""}`}
                    onClick={() => setSortBy((prev) => (prev === "RATING" ? "" : "RATING"))}
                  >
                    <i className="fa fa-star"></i> Star Rating
                  </button>
                </div>
                <div>
                  <button className="excel-btn" onClick={exportToExcel}>
                    <i className="fa fa-file-excel text-success me-1"></i> Export To Excel
                  </button>
                </div>
              </div>
            </div>

            <div className="box_shadow_wbg">


              <div className="reslt_loop">
                {filteredHotels.length > 0 ? (
                  <>
                    {filteredHotels.map((hotel, index) => (
                      <React.Fragment key={`${hotel.hotelid || hotel.hotelCode || 'hotel'}-${index}`}>
                        <div className="repeat_hotel">
                          <div className="row">
                            <div className="col-12 col-md-4 col-lg-4">
                              <div className="hotrl_img">
                                <img
                                  src={
                                    hotel.image ||
                                    "/assets/images/hotel/hotel.jpg"
                                  }
                                  className="img-full"
                                  alt="hotel"
                                />
                                <a
                                  className="viewDetails"
                                  href="javascript:void(0)"
                                  data-bs-toggle="modal"
                                  data-bs-target="#hotlDetail"
                                  onClick={() => setSelectedHotel(hotel)}
                                >
                                  Detail View
                                </a>
                              </div>
                            </div>
                            <div className="col-12 col-md-5 col-lg-5 mt-3 mt-md-0">
                              <div className="name_Address">
                                <h4>{hotel.hotelName}</h4>
                                <div className="start_cat">
                                  {[...Array(hotel.starRating || 0)].map(
                                    (_, i) => (
                                      <i key={i} className="star"></i>
                                    ),
                                  )}
                                </div>
                                <p className="loction">
                                  <i className="fa fa-location-dot"></i>
                                  {hotel.address}
                                </p>

                                {/* {(() => {
                                  const desc = hotel.description;
                                  if (!desc || typeof desc !== "object")
                                    return null;

                                  const snippet =
                                    desc.Snippet ||
                                    desc.Overview ||
                                    desc.location ||
                                    desc.headline ||
                                    Object.values(desc)[0];

                                  if (snippet && typeof snippet === "string") {
                                    return (
                                      <p className="htl_desc_snippet text-muted small mt-2 mb-2">
                                        {snippet.substring(0, 150)}
                                        {snippet.length > 150 ? "..." : ""}
                                      </p>
                                    );
                                  }
                                  return null;
                                })()} */}

                                <ul className="availble">
                                  {getAmenityIcons(hotel.amenities).map(
                                    (icon, i) => (
                                      <li key={i}>
                                        <img
                                          src={`/assets/images/hotel/${icon}`}
                                          alt="facility"
                                          onError={(e) => {
                                            e.target.src =
                                              "/assets/images/hotel/wifi.png";
                                          }}
                                        />
                                      </li>
                                    ),
                                  )}
                                </ul>

                                {/* {hotel.gst && (
                                  <div className="gst_pill mt-3">
                                    <span className="gst-pill-badge">
                                      <i className="fa fa-check-circle fs-12"></i>
                                      GST claim eligible rates available
                                    </span>
                                  </div>
                                )} */}

                                 {(() => {
                                   const summary = getCancellationSummary(hotel);
                                   if (!summary) return null;
                                   return (
                                     <div className={`cancellation-summary-pill mt-3 d-inline-flex align-items-center px-3 py-2 rounded ${summary.isFree ? "free" : "charge"}`}>
                                       <i className={`fa ${summary.isFree ? "fa-check-circle me-2" : "fa-info-circle me-2"}`}></i>
                                       {summary.text}
                                     </div>
                                   );
                                 })()}
                              </div>
                            </div>
                            <div className="col-12 col-md-3 col-lg-3 mb-4 mb-md-0">
                              <div className="price_views">
                                <p className="room_only">
                                  {hotel.mealBasis || "Room Only"}
                                  {hotel.refundable !== undefined && (
                                    <span
                                      className={`ms-2 badge text-white ${hotel.refundable ? "bg-success" : "bg-danger"}`}
                                    >
                                      {hotel.refundable
                                        ? "Refundable"
                                        : "Non-Refundable"}
                                    </span>
                                  )}
                                </p>
                                <p className="per_day">
                                  Price For {nightsCount}{" "}
                                  {nightsCount > 1 ? "Nights" : "Night"}
                                </p>
                                <h2 className="price">
                                  <i className="fa fa-inr"></i>
                                  {Math.round(hotel.hotelMinPrice || 0).toLocaleString()}{" "}
                                </h2>
                                <p className="taxes">
                                  Includes Taxes & Charges
                                </p>

                                <button
                                  className="choose_room"
                                  onClick={() => handleChooseRoom(hotel)}
                                >
                                  Choose Room
                                </button>

                                {hotel.policyType && (
                                  <div className="text-center mb-2">
                                    <span
                                      className={`fw-semibold ${hotel.policyType === "OutOfPolicy" ? "text-danger" : "text-success"}`}
                                      style={{ fontSize: "12px" }}
                                    >
                                      {hotel.policyType === "OutOfPolicy" ? (
                                        <>
                                          <i className="fa fa-times-circle me-1"></i>{" "}
                                          OutOfPolicy
                                        </>
                                      ) : (
                                        <>
                                          <i className="fa fa-check-circle me-1"></i>{" "}
                                          WithinPolicy
                                        </>
                                      )}
                                    </span>
                                  </div>
                                )}
                                {/* {hotel.policyType === 'OutOfPolicy' && hotel.policyReason && (
                                  <div className="text-danger mt-1 text-center" style={{ fontSize: "11px", lineHeight: "1.2" }}>
                                    {hotel.policyReason}
                                  </div>
                                )} */}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* {index === 2 && (
                          <div className="special_delas">
                            <h5 className="Collections">
                              <i className="star"></i> Collections
                            </h5>
                            <Swiper
                              modules={[Navigation, Autoplay]}
                              spaceBetween={10}
                              slidesPerView={4}
                              navigation
                              autoplay={{ delay: 332000 }}
                              loop={true}
                            >
                              <SwiperSlide>
                                <div className="box_iten">
                                  <div className="box_item_img">
                                    <img
                                      className="img-full"
                                      src="/assets/images/hotel/small-hotel.jpg"
                                      alt="hotel"
                                    />
                                  </div>
                                  <div className="box_item_text">
                                    <h4>Beach Paradise</h4>
                                    <p>Classic beach experience...</p>
                                  </div>
                                </div>
                              </SwiperSlide>
                            </Swiper>
                          </div>
                        )} */}
                      </React.Fragment>
                    ))}
                  </>
                ) : (
                  !loading && hotels.length > 0 && (
                    <div className="text-center py-5">
                      <h4>No hotels found matching your filters.</h4>
                    </div>
                  )
                )}
                {loading && (
                  <div className="text-center py-5">
                    <p className="text-center load">
                      <span className="loader"></span>
                    </p>
                    <h5 className="mt-3 text-muted">
                      Searching for the best hotels...
                    </h5>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Model Pop Up */}
      <div class="modal fade" id="hotlDetail">
        <div class="modal-dialog modal-xl">
          <div class="modal-content">
            <div class="modal-header">
              <h4 class="modal-title">
                {selectedHotel?.hotelName || "Hotel Details"}
              </h4>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>

            <div class="modal-body">
              <div className="row">
                <div className="col-12 col-md-8 col-lg-8">
                  <div className="imgSlider">
                    <Swiper
                      key={selectedHotel?.hotelid || "none"}
                      modules={[Navigation, Autoplay]}
                      spaceBetween={10}
                      slidesPerView={1}
                      navigation
                      autoplay={{ delay: 2000 }}
                      loop={true}
                      observer={true}
                      observeParents={true}
                    >
                      {selectedHotel?.allImages?.length > 0 ? (
                        selectedHotel.allImages.map((imgUrl, i) => (
                          <SwiperSlide key={i}>
                            <div className="imager">
                              <img
                                src={imgUrl}
                                className="img-full"
                                alt={`hotel-${i}`}
                                onError={(e) => {
                                  e.target.src =
                                    "/assets/images/hotel/hotel.jpg";
                                }}
                              />
                            </div>
                          </SwiperSlide>
                        ))
                      ) : (
                        <SwiperSlide>
                          <div className="imager">
                            <img
                              src="/assets/images/hotel/hotel.jpg"
                              className="img-full"
                              alt="hotel fallback"
                            />
                          </div>
                        </SwiperSlide>
                      )}
                    </Swiper>
                  </div>
                </div>
                <div className="col-12 col-md-4 col-lg-4">
                  <div className="contnt mt-5">
                    <h3 className="htl_name">{selectedHotel?.hotelName}</h3>
                    <div className="start_cat">
                      {[...Array(selectedHotel?.starRating || 0)].map(
                        (_, i) => (
                          <i key={i} className="star"></i>
                        ),
                      )}
                    </div>
                    <p className="htl_add">{selectedHotel?.address}</p>

                    <ul class="availble">
                      {getAmenityIcons(selectedHotel?.amenities).map(
                        (icon, i) => (
                          <li key={i}>
                            <img
                              src={`/assets/images/hotel/${icon}`}
                              alt="facility"
                              onError={(e) => {
                                e.target.src = "/assets/images/hotel/wifi.png";
                              }}
                            />
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                </div>
                <div className="col-12 col-md-12 col-lg-12">
                  <div className="content">
                    <h3 className="sub_heading">Overview & Directions</h3>
                    {selectedHotel?.description &&
                    typeof selectedHotel.description === "object" ? (
                      Object.entries(selectedHotel.description).map(
                        ([key, value]) => {
                          if (!value) return null;

                          // Format label: "business_amenities" -> "Business Amenities"
                          const label = key
                            .replace(/_/g, " ")
                            .replace(/([A-Z])/g, " $1") // Handle PascalCase
                            .trim()
                            .replace(/\b\w/g, (c) => c.toUpperCase());

                          // If it's the main Overview, just show the text without label if it's the only thing
                          if (
                            key.toLowerCase() === "overview" &&
                            Object.keys(selectedHotel.description).length === 1
                          ) {
                            return (
                              <p key={key} className="mb-4 text-gray-800">
                                {value}
                              </p>
                            );
                          }

                          return (
                            <p key={key} className="mb-4 text-gray-800">
                              <strong className="text-dark">{label} :</strong>{" "}
                              {value}
                            </p>
                          );
                        },
                      )
                    ) : (
                      <p>
                        {selectedHotel?.description ||
                          "No description available."}
                      </p>
                    )}

                    <h3 className="sub_heading">Amenities</h3>
                    <div className="row amenities_list">
                      {selectedHotel?.amenities?.map((amenity, i) => (
                        <div
                          key={i}
                          className="col-12 col-md-4 col-sm-6 mb-2 d-flex align-items-center"
                        >
                          <span
                            className="me-2"
                            style={{ color: "#333", fontSize: "1.2rem" }}
                          >
                            •
                          </span>
                          <span className="small text-gray-700">{amenity}</span>
                        </div>
                      ))}
                      {!selectedHotel?.amenities?.length && (
                        <div className="col-md-12 text-muted small">
                          No amenities listed.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alternative Hotel Modal */}
      {showAlternativeModal && pendingHotelSelection && bestAlternativeHotel && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title text-white">Recommended Alternative</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowAlternativeModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="alert alert-info mb-4">
                  {pendingHotelSelection.policyType === "OutOfPolicy" ? (
                    <span>
                      <i className="fa fa-info-circle me-2"></i>
                      The hotel you selected is <strong>Out of Policy</strong>. We found a great In-Policy alternative within a 3km radius with the same star rating!
                    </span>
                  ) : (
                    <span>
                      <i className="fa fa-info-circle me-2"></i>
                      We found a cheaper alternative within a 3km radius with the same star rating!
                    </span>
                  )}
                </div>

                <div className="row g-4">
                  {/* Selected Hotel */}
                  <div className="col-12 col-md-6 mb-3 mb-md-0">
                    <div className="card h-100 border-warning">
                      <div className="card-header bg-warning text-dark fw-bold d-flex justify-content-between align-items-center py-2">
                        Your Selection
                      </div>
                      <img
                        src={pendingHotelSelection.image || "/assets/images/hotel/hotel.jpg"}
                        className="card-img-top"
                        alt="Selected Hotel"
                        style={{ height: "150px", objectFit: "cover" }}
                        onError={(e) => {
                          e.target.src = "/assets/images/hotel/hotel.jpg";
                        }}
                      />
                      <div className="card-body d-flex flex-column justify-content-between p-3">
                        <div>
                          <h6 className="card-title fw-bold m-0" style={{ color: "#110942" }}>
                            {pendingHotelSelection.hotelName}
                          </h6>
                          <div className="text-warning my-1">
                            {[...Array(pendingHotelSelection.starRating || 0)].map((_, i) => (
                              <i key={i} className="fa fa-star star-icon"></i>
                            ))}
                          </div>
                          <p className="small text-muted mb-2">
                            <i className="fa fa-map-marker-alt me-1"></i>
                            {pendingHotelSelection.address?.substring(0, 50)}...
                          </p>
                        </div>
                        <div>
                          <h5 className="fw-bold mb-2" style={{ color: "#110942" }}>
                            <i className="fa fa-inr"></i> {Math.round(pendingHotelSelection.hotelMinPrice || 0).toLocaleString()}
                          </h5>
                          {pendingHotelSelection.policyType && (
                            <span className={`badge ${pendingHotelSelection.policyType === 'OutOfPolicy' ? 'bg-danger' : 'bg-success'}`}>
                              {pendingHotelSelection.policyType}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="card-footer bg-white border-top-0 p-3 pt-0 text-center">
                        <div className="form-check d-inline-block text-start">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="hotelAlternativeSelection"
                            id="originalSelection"
                            checked={selectedAlternativeOption === "original"}
                            onChange={() => setSelectedAlternativeOption("original")}
                            style={{ cursor: "pointer", border: "1px solid #110942" }}
                          />
                          <label className="form-check-label fw-bold" htmlFor="originalSelection" style={{ cursor: "pointer", color: "#110942" }}>
                            Keep Original
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Best Alternative Hotel */}
                  <div className="col-12 col-md-6 mb-3 mb-md-0">
                    <div className="card h-100 border-success shadow">
                      <div className="card-header bg-success text-white fw-bold d-flex justify-content-between align-items-center py-2">
                        <span>Recommended</span>
                        <span className="badge bg-white text-success py-1">Lowest Fare</span>
                      </div>
                      <img
                        src={bestAlternativeHotel.image || "/assets/images/hotel/hotel.jpg"}
                        className="card-img-top"
                        alt="Alternative Hotel"
                        style={{ height: "150px", objectFit: "cover" }}
                        onError={(e) => {
                          e.target.src = "/assets/images/hotel/hotel.jpg";
                        }}
                      />
                      <div className="card-body d-flex flex-column justify-content-between p-3">
                        <div>
                          <h6 className="card-title fw-bold text-success m-0">
                            {bestAlternativeHotel.hotelName}
                          </h6>
                          <div className="text-warning my-1">
                            {[...Array(bestAlternativeHotel.starRating || 0)].map((_, i) => (
                              <i key={i} className="fa fa-star star-icon"></i>
                            ))}
                          </div>
                          <p className="small text-muted mb-2">
                            <i className="fa fa-map-marker-alt me-1"></i>
                            {bestAlternativeHotel.address?.substring(0, 50)}...
                          </p>
                        </div>
                        <div>
                          {(() => {
                            const savings = Math.round((pendingHotelSelection?.hotelMinPrice || 0) - (bestAlternativeHotel?.hotelMinPrice || 0));
                            return (
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="fw-bold text-success mb-0">
                                  <i className="fa fa-inr"></i> {Math.round(bestAlternativeHotel.hotelMinPrice || 0).toLocaleString()}
                                </h5>
                                {savings > 0 && (
                                  <div className="text-end">
                                    <span className="fw-bold text-primary" style={{ fontSize: "11px", display: "block" }}>
                                      Recommended
                                    </span>
                                    <span className="fw-bold text-danger" style={{ fontSize: "11px", display: "block" }}>
                                      Save <i className="fa fa-inr"></i> {savings.toLocaleString()}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          <div className="d-flex gap-2 flex-wrap">
                            {bestAlternativeHotel.policyType && (
                              <span className="badge bg-success">
                                {bestAlternativeHotel.policyType}
                              </span>
                            )}
                            {bestAlternativeHotel.calculatedDistance && (
                              <span className="badge bg-info text-white">
                                <i className="fa fa-location-arrow me-1"></i>
                                {bestAlternativeHotel.calculatedDistance} km away
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="card-footer bg-white border-top-0 p-3 pt-0 text-center">
                        <div className="form-check d-inline-block text-start">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="hotelAlternativeSelection"
                            id="recommendedSelection"
                            checked={selectedAlternativeOption === "Recommended"}
                            onChange={() => setSelectedAlternativeOption("Recommended")}
                            style={{ cursor: "pointer", border: "1px solid #198754" }}
                          />
                          <label className="form-check-label fw-bold text-success" htmlFor="recommendedSelection" style={{ cursor: "pointer", color: "#198754" }}>
                            Choose Recommended
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedAlternativeOption === "original" && (
                  <div className="mt-4">
                    <label htmlFor="alternativeRemarksReason" className="form-label fw-bold text-danger mb-1">
                      Reason for keeping selection <span className="text-danger">*</span>
                    </label>
                    <select
                      id="alternativeRemarksReason"
                      className="form-select mb-3"
                      value={selectedReason}
                      onChange={(e) => setSelectedReason(e.target.value)}
                    >
                      <option value="">--Select--</option>
                      <option value="Does Not Suit With My Schedule">Does Not Suit With My Schedule</option>
                      <option value="Hotel Preference">Hotel Preference</option>
                      <option value="Safety And Security">Safety And Security</option>
                      <option value="Travelling With Client">Travelling With Client</option>
                      <option value="Travelling With Seniors">Travelling With Seniors</option>
                      <option value="Other">Other</option>
                    </select>

                    {selectedReason === "Other" && (
                      <div>
                        <label htmlFor="alternativeRemarksCustom" className="form-label fw-bold text-danger mb-1">
                          Remarks <span className="text-danger">*</span>
                        </label>
                        <textarea
                          id="alternativeRemarksCustom"
                          className="form-control"
                          rows="2"
                          placeholder="Enter reason for keeping your selection instead of the recommended alternative"
                          value={customRemarks}
                          onChange={(e) => setCustomRemarks(e.target.value)}
                        ></textarea>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-4 text-end border-top pt-3">
                  <button
                    className="btn btn-primary px-5 py-2 fw-bold"
                    style={{ borderRadius: "20px" }}
                    onClick={() => {
                      const selectedHotel = selectedAlternativeOption === "original" ? pendingHotelSelection : bestAlternativeHotel;
                      proceedWithHotelSelection(selectedHotel, selectedAlternativeOption);
                    }}
                  >
                    Continue <i className="fas fa-arrow-right ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Results;
